<template>
  <div class="layout-container flex-equal" :class="flexFlow">
    <div
      v-for="entry in items"
      :key="entry.key"
      class="d-flex flex-equal layout-cell"
      :class="{ 'layout-cell--hidden': entry.hidden }"
    >
      <layout-grid
        v-if="entry.item.type === 'layout'"
        :layout="entry.item as Layout"
      />
      <LayoutGridItem
        v-else
        class="layout-item"
        :view-id="entry.viewId!"
        @pointerdown.capture="onFocusView(entry.viewId!)"
        @dblclick="maximize(entry.viewId!)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, toRefs } from 'vue';
import { Layout, LayoutItem } from '@/src/types/layout';
import { useViewStore } from '@/src/store/views';
import { useToolStore } from '@/src/store/tools';
import { Tools } from '@/src/store/tools/types';
import LayoutGridItem from '@/src/components/LayoutGridItem.vue';

// True when this branch of the layout tree contains the maximized view
// (or its leaf slot is the maximized view). Used to hide entire sub-trees
// via display:none so the surviving branch flexes to fill the container,
// without unmounting any view components.
function layoutContainsView(
  item: LayoutItem,
  viewId: string,
  slots: readonly string[]
): boolean {
  if (item.type === 'slot') {
    return slots[item.slotIndex] === viewId;
  }
  return item.items.some((child) => layoutContainsView(child, viewId, slots));
}

export default defineComponent({
  name: 'LayoutGrid',
  components: {
    LayoutGridItem,
  },
  methods: {
    onFocusView(id: string) {
      useViewStore().setActiveView(id);
    },
    maximize(id: string) {
      const currentTool = useToolStore().currentTool;
      if (currentTool !== Tools.Polygon) {
        useViewStore().setActiveView(id);
        useViewStore().toggleActiveViewMaximized();
      }
    },
  },
  props: {
    layout: {
      type: Object as PropType<Layout>,
      required: true,
    },
  },
  setup(props) {
    const { layout } = toRefs(props);
    const viewStore = useViewStore();

    const flexFlow = computed(() => {
      return layout.value.direction === 'column' ? 'flex-column' : 'flex-row';
    });

    // Each entry carries a stable key (viewId for leaf slots, structural key
    // for nested layouts) and the per-branch `hidden` flag that drives
    // display:none. Keying by viewId keeps each LayoutGridItem mounted across
    // maximize toggles instead of being recreated by Vue.
    const items = computed(() => {
      const maximizedId = viewStore.maximizedViewId;
      const slots = viewStore.layoutSlots;
      return layout.value.items.map((item, idx) => {
        if (item.type === 'slot') {
          const viewId = slots[item.slotIndex];
          const hidden = !!maximizedId && viewId !== maximizedId;
          return {
            item,
            key: `slot-${viewId ?? idx}`,
            viewId,
            hidden,
          };
        }
        const hidden =
          !!maximizedId && !layoutContainsView(item, maximizedId, slots);
        return {
          item,
          key: `layout-${idx}`,
          viewId: undefined,
          hidden,
        };
      });
    });

    return {
      items,
      flexFlow,
    };
  },
});
</script>

<style scoped src="@/src/components/styles/utils.css"></style>

<style scoped>
.layout-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.layout-cell {
  gap: 6px;
}

/* Each cell participates in flex layout normally. Hiding via display:none
   lets the surviving siblings flex to fill the freed space, so a maximized
   leaf naturally grows to the outer container without rebuilding the tree. */
.layout-cell--hidden {
  display: none !important;
}

.layout-item {
  display: flex;
  flex: 1;
}
</style>
